import React, { useMemo } from 'react'

import { Box, Divider, Stack, Typography } from '@mui/material'

import { DiscontinuedSkullIcon } from '../../../../components/DiscontinuedSkullIcon/DiscontinuedSkullIcon'
import { PlainTextParagraphs } from '../../../../components/PlainTextParagraphs'
import { sanitizeHtml } from '../../../../helpers/dom'
import { isHtmlBlank } from '../../../../helpers/string'
import { sectionLabelSx } from '../helpers/typographyStyles'

// Wide enough for two stat tiles side by side; the tooltip's own max-width is
// disabled at the call site so this is the single place the width is decided.
const CARD_WIDTH = 430

interface DescriptionHoverCardProps {
  name: string
  // Ancestors only, outermost first. Empty for top-level rows, which drops the
  // breadcrumb (and its color dot) rather than leaving it dangling.
  trail: string[]
  colorHex: string
  discontinued?: boolean
  // Secondary line under the name, e.g. what a group row contains.
  subtitle?: string
  description?: string
  descriptionLabel: string
  // Blueprint copy is authored as HTML in the blueprint glossary; event type copy
  // is plain text in the language map, so only the former is injected.
  isRichTextDescription: boolean
  // Content below the description (e.g. stat tiles).
  children?: React.ReactNode
}

/**
 * Hover card header for a taxonomy entry: where it sits (breadcrumb), its name,
 * and its editorial description. The description is optional: an empty,
 * whitespace-only or (for rich text) HTML-empty one is treated as missing and renders no section at all. Shared by the catalogue tree's row card, which
 * adds stat tiles as `children`, and the description-only cards on tables that
 * already show the figures in their columns.
 *
 * Deliberately non-interactive: a tooltip's content can't be reached with the
 * pointer, so nothing inside may be a link or carry its own tooltip.
 */
const DescriptionHoverCardComponent: React.FC<DescriptionHoverCardProps> = ({
  name,
  trail,
  colorHex,
  discontinued,
  subtitle,
  description,
  descriptionLabel,
  isRichTextDescription,
  children,
}) => {
  // Sanitized here rather than by the caller so DOMPurify only runs for the row
  // actually being hovered.
  const hasDescription = isRichTextDescription ? !isHtmlBlank(description) : Boolean(description?.trim())
  const sanitizedDescription = useMemo(
    () => (description && hasDescription && isRichTextDescription ? sanitizeHtml(description) : ''),
    [description, hasDescription, isRichTextDescription]
  )
  const hasBody = hasDescription || Boolean(children)

  return (
    <Box sx={{ width: CARD_WIDTH, maxWidth: '100%' }}>
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {trail.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colorHex, flexShrink: 0 }} />
            <Typography sx={sectionLabelSx} noWrap>
              {trail.join(' › ')}
            </Typography>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.2 }}>
            {name}
          </Typography>
          {discontinued ? <DiscontinuedSkullIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} /> : null}
        </Stack>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Stack>

      {/* Full-bleed across the tooltip Paper's own padding, matching the seam
          between header copy and content on the analytics cards. Dropped when
          nothing follows it, so a card without a description ends at the header. */}
      {hasBody ? <Divider sx={{ mx: -2, my: 1.5 }} /> : null}

      {hasDescription && description ? (
        <Box sx={{ mb: children ? 2 : 0 }}>
          <Typography sx={{ ...sectionLabelSx, mb: 0.5 }}>{descriptionLabel}</Typography>
          {/* `overflowWrap` so an unbroken token (URL, long compound name) wraps
              inside the fixed card width instead of running past the tooltip edge. */}
          {isRichTextDescription ? (
            <Typography
              variant="body2"
              component="div"
              sx={{ '& p': { m: 0 }, overflowWrap: 'anywhere' }}
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          ) : (
            <PlainTextParagraphs text={description} sx={{ overflowWrap: 'anywhere' }} />
          )}
        </Box>
      ) : null}

      {children}
    </Box>
  )
}

export const DescriptionHoverCard = React.memo(DescriptionHoverCardComponent)
DescriptionHoverCard.displayName = 'DescriptionHoverCard'
